import { useCallback, useEffect, useRef, useState } from "react";
import { createSpectateSocket } from "./connection";
import { queueEvent } from "../analytics";
import { EVENTS } from "../analytics/events";

const storageKey = (code) => `live:${code}`;

const loadIdentity = (code) => {
  try {
    const raw = localStorage.getItem(storageKey(code));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const saveIdentity = (code, identity) => {
  try {
    localStorage.setItem(storageKey(code), JSON.stringify(identity));
  } catch {
    /* private mode etc. — refresh just re-asks for a name */
  }
};

/**
 * Viewer-side state machine for a live spectate session.
 *
 * phase: connecting → join | live | paused | ended | notFound
 * Returns { phase, meta, state, votes, presence, identity, myVote,
 *           joining, joinError, join(name, teamId), vote(group, optionKey) }.
 *
 * - snapshot on join, then incremental live:state patches (stale seq dropped)
 * - voteEpoch bump clears myVote instantly (all votes reset on screen change)
 * - socket.io auto-reconnect re-runs the hello flow; a stored identity
 *   silently rejoins the same locked team
 */
export default function useLiveSession(code) {
  const [phase, setPhase] = useState("connecting");
  const [meta, setMeta] = useState(null); // { gameName, teams } pre-join
  const [state, setState] = useState(null); // neutral live-state doc
  const [votes, setVotes] = useState(null); // { voteEpoch, group, counts, total }
  const [presence, setPresence] = useState({ total: 0, byTeam: {} });
  const [identity, setIdentity] = useState(() => loadIdentity(code));
  const [myVote, setMyVote] = useState(null);
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState(null);

  const socketRef = useRef(null);
  const seqRef = useRef(0);
  const epochRef = useRef(0);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  const applySnapshot = useCallback((snap) => {
    seqRef.current = snap.state.seq;
    epochRef.current = snap.state.voteEpoch;
    setState(snap.state);
    setVotes(snap.votes || null);
    setPresence(snap.presence || { total: 0, byTeam: {} });
    setMyVote(null);
    setPhase(snap.state.screen === "paused" ? "paused" : "live");
  }, []);

  useEffect(() => {
    if (!code) return undefined;
    const socket = createSpectateSocket(code, loadIdentity(code) || {});
    socketRef.current = socket;

    const hello = () => {
      socket.emit("viewer:hello", {}, (res) => {
        if (!res || res.error) {
          setPhase(res?.error === "session_not_found" ? "notFound" : "ended");
          return;
        }
        setMeta({ gameName: res.gameName, teams: res.teams });
        if (res.joined) {
          const id = { viewerId: res.viewerId, name: res.name, teamId: res.teamId };
          setIdentity(id);
          saveIdentity(code, id);
          applySnapshot(res);
        } else if (phaseRef.current === "connecting") {
          setPhase("join");
        }
      });
    };

    socket.on("connect", hello);

    socket.on("connect_error", (err) => {
      if (err?.message === "session_not_found") {
        setPhase((p) => (p === "live" || p === "paused" ? "ended" : "notFound"));
        socket.close();
      }
      // other errors: socket.io keeps retrying on its own
    });

    socket.on("live:state", ({ seq, voteEpoch, patch }) => {
      if (typeof seq !== "number" || seq <= seqRef.current) return; // stale
      seqRef.current = seq;
      if (voteEpoch !== epochRef.current) {
        epochRef.current = voteEpoch;
        setMyVote(null); // votes reset on every screen change
        setVotes(null);
      }
      setState((prev) => ({ ...(prev || {}), ...patch, seq, voteEpoch }));
      setPhase((p) => (p === "live" || p === "paused" ? "live" : p));
    });

    socket.on("live:votes", (payload) => {
      if (payload.voteEpoch !== epochRef.current) return;
      setVotes(payload);
    });

    socket.on("live:presence", ({ total, byTeam }) => {
      setPresence({ total: total || 0, byTeam: byTeam || {} });
    });

    socket.on("live:paused", () => {
      setPhase((p) => (p === "live" ? "paused" : p));
    });

    socket.on("live:ended", () => {
      setPhase("ended");
      socket.close();
    });

    return () => {
      socket.removeAllListeners();
      socket.close();
      socketRef.current = null;
    };
  }, [code, applySnapshot]);

  const join = useCallback(
    (name, teamId) => {
      const socket = socketRef.current;
      if (!socket) return;
      setJoining(true);
      setJoinError(null);
      socket.emit("viewer:join", { name, teamId }, (res) => {
        setJoining(false);
        if (!res?.ok) {
          setJoinError(res?.reason || "server_error");
          return;
        }
        const id = { viewerId: res.viewerId, name: res.name, teamId: res.teamId };
        setIdentity(id);
        saveIdentity(code, id);
        applySnapshot(res);
        queueEvent(EVENTS.LIVE_VIEWER_JOINED, {
          sessionCode: code,
          team: res.teamId,
        });
      });
    },
    [code, applySnapshot]
  );

  /** Tap to vote; tapping your current vote again removes it. */
  const vote = useCallback(
    (group, optionKey) => {
      const socket = socketRef.current;
      if (!socket) return;
      const voteEpoch = epochRef.current;
      if (myVote === optionKey) {
        socket.emit("viewer:unvote", { voteEpoch }, (res) => {
          if (res?.ok) setMyVote(null);
        });
        return;
      }
      socket.emit("viewer:vote", { voteEpoch, group, optionKey }, (res) => {
        if (res?.ok) {
          setMyVote(optionKey);
          queueEvent(EVENTS.LIVE_VOTE_CAST, { group });
        }
      });
    },
    [myVote]
  );

  return {
    phase,
    meta,
    state,
    votes,
    presence,
    identity,
    myVote,
    joining,
    joinError,
    join,
    vote,
  };
}
